import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Check, Minus } from '@phosphor-icons/react';

/*
 * 18px checkbox with a real mixed state.
 *
 * `indeterminate` is NOT a React attribute — it only exists on the DOM node, so
 * it is set through a ref. Drive it from `granted > 0 && granted < total`.
 *
 * Spec: core-handover/02-components.md
 */
const Checkbox = React.forwardRef(
  (
    { checked, indeterminate, disabled, denied, label, className, ...props },
    ref,
  ) => {
    const inner = React.useRef(null);
    const node = ref || inner;

    React.useEffect(() => {
      if (node.current) node.current.indeterminate = Boolean(indeterminate);
    }, [indeterminate, node]);

    const marked = checked || indeterminate;

    return (
      <span className={cn('relative inline-flex h-[18px] w-[18px]', className)}>
        <input
          ref={node}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-label={label}
          className={cn(
            'peer appearance-none h-[18px] w-[18px] rounded-sm border transition-colors',
            // @tailwindcss/forms fills :checked and :indeterminate boxes with
            // `currentColor` from an attribute selector, which outranks any
            // bg-* utility. Set the text colour and let it resolve to brand;
            // bg-none kills the plugin's own tick so it can't double up with
            // the Phosphor glyph.
            'text-brand-600 bg-none',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2',
            // A granted cell always reads as granted, editable or not — the
            // matrix is read most of the time. Disabled only removes the
            // affordance; `denied` (never grantable) reads lighter still.
            marked && 'border-brand-600 bg-brand-600',
            !marked && denied && 'bg-white border-neutral-300',
            !marked &&
              !denied &&
              disabled &&
              'bg-neutral-100 border-neutral-200',
            !marked && !denied && !disabled && 'bg-white border-neutral-400',
            disabled ? 'cursor-default' : 'cursor-pointer',
            disabled && marked && 'opacity-90',
          )}
          {...props}
        />
        {marked && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-white">
            {indeterminate ? (
              <Minus size={12} weight="bold" />
            ) : (
              <Check size={12} weight="bold" />
            )}
          </span>
        )}
      </span>
    );
  },
);

Checkbox.defaultProps = {
  checked: false,
  indeterminate: false,
  disabled: false,
  denied: false,
  label: '',
  className: '',
};

Checkbox.propTypes = {
  checked: PropTypes.bool,
  indeterminate: PropTypes.bool,
  disabled: PropTypes.bool,
  denied: PropTypes.bool,
  label: PropTypes.string,
  className: PropTypes.string,
};

export default Checkbox;
